import React from 'react';
import { BookOpen, Printer } from 'lucide-react';
import { useT } from '../../i18n';
import { PageHead, Card, HELP, Fade } from './os2ui';

/* Glossary — every business concept, documented in plain language. Printable. */

const GROUPS = [
  { en: 'Money', es: 'Dinero', keys: ['revenue', 'mrr', 'arr', 'pipeline_value', 'payouts'] },
  { en: 'Sales', es: 'Ventas', keys: ['lead', 'leads_today', 'stage', 'probability', 'lead_score', 'opportunities', 'meetings', 'customers', 'conversion', 'next_action', 'agent', 'last_activity'] },
  { en: 'Index AI & Journey', es: 'Index AI y Recorrido', keys: ['index_ai_score', 'scan', 'report', 'journey', 'source', 'affiliate', 'campaign', 'referrals'] },
  { en: 'Email & Web', es: 'Email y Web', keys: ['email_sent', 'email_opened', 'email_clicked', 'open_rate', 'click_rate', 'page_views', 'read_time', 'whatsapp_clicks', 'intents'] },
  { en: 'Health', es: 'Salud', keys: ['health'] },
];

const NAMES = {
  en: {
    revenue: 'Revenue', mrr: 'MRR', arr: 'ARR', pipeline_value: 'Pipeline Value', payouts: 'Partner Payouts',
    lead: 'Lead', leads_today: 'Leads Today', stage: 'Stage', probability: 'Probability', lead_score: 'Lead Score',
    opportunities: 'Open Opportunities', meetings: 'Meetings', customers: 'Customers', conversion: 'Conversion Rates',
    next_action: 'Next Action', agent: 'Assigned Agent', last_activity: 'Last Activity',
    index_ai_score: 'Index AI Score', scan: 'Scan Completed', report: 'Report Downloaded', journey: 'Journey',
    source: 'Source', affiliate: 'Affiliate', campaign: 'Campaign / UTM', referrals: 'Affiliate Referrals',
    email_sent: 'Email Sent', email_opened: 'Email Opened', email_clicked: 'Email Clicked',
    open_rate: 'Open Rate', click_rate: 'Click Rate', page_views: 'Page Views', read_time: 'Read Time',
    whatsapp_clicks: 'WhatsApp Clicks', intents: 'Visitor Intents', health: 'Company Health Score',
  },
  es: {
    revenue: 'Ingresos', mrr: 'MRR', arr: 'ARR', pipeline_value: 'Valor del Pipeline', payouts: 'Comisiones a Partners',
    lead: 'Lead', leads_today: 'Leads Hoy', stage: 'Etapa', probability: 'Probabilidad', lead_score: 'Lead Score',
    opportunities: 'Oportunidades Abiertas', meetings: 'Reuniones', customers: 'Clientes', conversion: 'Tasas de Conversión',
    next_action: 'Próxima Acción', agent: 'Agente Asignado', last_activity: 'Última Actividad',
    index_ai_score: 'Index AI Score', scan: 'Scan Completado', report: 'Reporte Descargado', journey: 'Recorrido',
    source: 'Fuente', affiliate: 'Afiliado', campaign: 'Campaña / UTM', referrals: 'Referidos de Afiliados',
    email_sent: 'Email Enviado', email_opened: 'Email Abierto', email_clicked: 'Email Clicado',
    open_rate: 'Tasa de Apertura', click_rate: 'Tasa de Clic', page_views: 'Páginas Vistas', read_time: 'Tiempo de Lectura',
    whatsapp_clicks: 'Clics a WhatsApp', intents: 'Intención de Visitantes', health: 'Salud de la Empresa',
  },
};

export default function GlossaryPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const l = isEs ? 'es' : 'en';

  return (
    <div data-testid="os2-glossary">
      <PageHead
        title={isEs ? 'Glosario' : 'Glossary'}
        question={isEs ? 'Cada concepto del negocio, explicado en lenguaje sencillo.' : 'Every business concept, explained in plain language.'}
        right={
          <button onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white hover:border-gray-300 text-gray-700 text-[13px] font-semibold px-4 py-2 transition-colors"
            data-testid="glossary-print">
            <Printer className="w-4 h-4" /> {isEs ? 'Imprimir / PDF' : 'Print / PDF'}
          </button>
        }
      />
      {GROUPS.map((g, gi) => (
        <Fade key={g.en} delay={gi * 0.06}>
          <p className="mt-6 mb-2 text-[11px] tracking-[0.18em] uppercase text-[#F77F11] font-bold flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" /> {isEs ? g.es : g.en}
          </p>
          <Card className="divide-y divide-gray-100">
            {g.keys.map((k) => (
              <div key={k} className="px-5 py-3 grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-1 sm:gap-4" data-testid={`glossary-${k}`}>
                <p className="text-[13.5px] font-semibold text-gray-900">{NAMES[l][k]}</p>
                <p className="text-[13px] text-gray-600 leading-relaxed">{HELP[l][k]}</p>
              </div>
            ))}
          </Card>
        </Fade>
      ))}
    </div>
  );
}
